/**
 * @file Inline block of 4 circular contact-channel buttons (TG / WA /
 *   Max / Phone). Reusable across tools pages — wherever a "secondary"
 *   contact affordance is needed below a primary lead form.
 *
 * @important Distinct from FloatingMessengers FAB. Both sources draw
 *   from src/lib/messengerChannels.tsx; FAB uses MESSENGER_CHANNELS (3),
 *   this uses CONTACT_CHANNELS (4 — includes Phone).
 *
 * @rule Button size h-11 w-11 = 44px = Apple HIG minimum tap target.
 *   Do NOT shrink below 44px. If layout pressure demands smaller —
 *   reconsider whether 4 channels are needed at all in that context
 *   (the answer is usually "no — show 3 messenger FAB instead").
 *
 * @rule Phone channel (kind === "phone") MUST NOT receive
 *   target="_blank" or rel="noopener noreferrer". The tel: URL scheme
 *   opens the device dialer, not a browser tab — target="_blank" on
 *   tel: produces an empty about:blank tab on desktop browsers
 *   alongside the dialer prompt, broken UX.
 */

"use client";

import { CONTACT_CHANNELS } from "@/lib/messengerChannels";
import { trackGoal } from "@/lib/metrika";

// @rule File-local map from CONTACT_CHANNELS channel name to Metrika goal
//       identifier for the contact-channels-block surface (rendered under
//       lead forms). ONLY messenger channels (Telegram/WhatsApp/Max) have
//       surface-specific goals here — the Phone channel is intentionally
//       absent. Phone clicks from this widget fire only the universal
//       `phone_click` counter (no `phone_contact_channels` exists in the
//       44-goal Metrika plan; per-surface phone attribution from this
//       widget would risk silent contamination if ContactChannels is
//       added to multiple surfaces). If per-surface phone attribution
//       becomes important, add `Позвонить: "phone_contact_channels"`
//       here AND create that goal in Metrika UI.
const CONTACT_GOAL_BY_NAME: Record<string, string> = {
  Telegram: "tg_contact_channels",
  WhatsApp: "wa_contact_channels",
  Max: "max_contact_channels",
};

interface ContactChannelsProps {
  /** Optional intro label rendered above the row. Default: "Или свяжитесь напрямую". Pass empty string to suppress. */
  label?: string;
  /** Horizontal alignment of the row of buttons. Default: "center". */
  align?: "center" | "start";
}

/**
 * Renders 4 circular contact-channel buttons (Telegram, WhatsApp, Max,
 * Phone) with an optional intro label above. The data comes from the
 * shared CONTACT_CHANNELS source — adding a new channel there
 * automatically appears here without component changes.
 */
export default function ContactChannels({
  label = "Или свяжитесь напрямую",
  align = "center",
}: ContactChannelsProps) {
  const justifyClass = align === "center" ? "justify-center" : "justify-start";
  const labelAlignClass = align === "center" ? "text-center" : "";

  return (
    <div className="mt-4">
      {label && (
        <p className={`mb-2 text-xs text-text-muted ${labelAlignClass}`}>
          {label}
        </p>
      )}
      <div className={`flex ${justifyClass} gap-3`}>
        {CONTACT_CHANNELS.map((ch) => (
          <a
            key={ch.name}
            href={ch.href}
            target={ch.kind === "phone" ? undefined : "_blank"}
            rel={ch.kind === "phone" ? undefined : "noopener noreferrer"}
            aria-label={ch.ariaLabel}
            onClick={() => {
              trackGoal(ch.kind === "phone" ? "phone_click" : "messenger_click");
              const specific = CONTACT_GOAL_BY_NAME[ch.name];
              if (specific) trackGoal(specific);
            }}
            className={`flex h-11 w-11 items-center justify-center rounded-full text-white shadow-sm transition-transform duration-200 hover:scale-110 ${ch.bg}`}
          >
            {ch.glyph}
          </a>
        ))}
      </div>
    </div>
  );
}
